import { useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Button, Chip, Alert, CircularProgress,
  IconButton, TextField, MenuItem, Tabs, Tab, Menu,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import {
  listTransactions, conciliar,
  Transaction, TxnStatus, ConciliarResult,
} from "../services/reconciliations";
import { MovimientosView } from "./Movimientos";
import { importeSearchText } from "../lib/importeSearch";
import { rangoPorDefecto, esRangoPorDefecto, TOPE_PAGINAS, FILAS_POR_PAGINA, TOPE_FILAS } from "../lib/bindRango";

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 });
}

function TxnStatusChip({ status }: { status: TxnStatus }) {
  const colors: Record<TxnStatus, [string, string, string]> = {
    pendiente:   ["#475569", "#F1F5F9", "Pendiente"],
    conciliada:  ["#166534", "#DCFCE7", "Conciliada"],
    perdida:     ["#B91C1C", "#FEE2E2", "Perdida"],
    redisparada: ["#9A3412", "#FFEDD5", "Re-disparada"],
  };
  const [fg, bg, label] = colors[status] ?? ["#333", "#EEE", status];
  return (
    <Chip label={label} size="small"
      sx={{ fontWeight: 700, fontSize: 11, height: 24, borderRadius: 3, color: fg, bgcolor: bg }} />
  );
}

export default function BindOperations() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Bind · Movimientos</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Transferencias" />
        <Tab label="Otras Transferencias" />
      </Tabs>
      {tab === 0 ? <TransactionsView /> : <MovimientosView />}
    </Box>
  );
}

// ===================== Transferencias (con conciliación) =====================
function TransactionsView() {
  const [rows,    setRows]    = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [err,     setErr]     = useState<string | null>(null);
  const [account, setAccount] = useState<"" | "45" | "84">("45");
  const [status,  setStatus]  = useState<"" | TxnStatus>("");
  const [from,    setFrom]    = useState<string>(() => rangoPorDefecto().from);   // YYYY-MM-DD · CP-480: últimas 24 h por defecto
  const [to,      setTo]      = useState<string>(() => rangoPorDefecto().to);
  // CP-480: la carga sin fechas llegó al tope de páginas → resultado incompleto.
  const [truncado, setTruncado] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conciliando, setConciliando] = useState(false);
  const [result,      setResult]      = useState<ConciliarResult | null>(null);
  const [dlAnchor,    setDlAnchor]    = useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);

  // Filtro por descripción + búsqueda libre (client-side sobre las filas cargadas).
  const [desc,      setDesc]      = useState<string>("");
  const [q,         setQ]         = useState<string>("");

  // Orden (default: fecha valor descendente) + paginado de display client-side.
  const [sortField, setSortField] = useState<"fecha_valor" | "importe">("fecha_valor");
  const [sortDir,   setSortDir]   = useState<"asc" | "desc">("desc");
  const [shown,     setShown]     = useState(100);

  const needsAccount = !account && status !== "perdida";
  const searching    = !!(q.trim() || desc);

  function toggleSort(field: "fecha_valor" | "importe") {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
    setShown(100);
  }
  const sortArrow = (field: "fecha_valor" | "importe") =>
    sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  function commonFilter() {
    return {
      account: account || undefined,
      status:  status || undefined,
      from:    from || undefined,
      to:      to || undefined,
    } as const;
  }

  // Trae TODAS las filas del filtro actual (paginando) → permite ordenar/buscar global.
  async function loadAll() {
    if (needsAccount) { setRows([]); return; }
    setLoading(true); setErr(null); setLoadedCount(0);
    try {
      setTruncado(false);
      setRows(await fetchAll(setLoadedCount, () => setTruncado(true)));
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }

  // Cambió un filtro server-side: traigo TODO el set (para poder ordenar/buscar global).
  // La búsqueda y el filtro de descripción se aplican client-side sobre lo cargado.
  useEffect(() => {
    loadAll();
    setShown(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, status, from, to]);

  // Opciones del filtro de descripción (de lo cargado) + filtrado client-side.
  const descOptions = useMemo(
    () => Array.from(new Set(rows.map((t) => t.descripcion).filter(Boolean) as string[])).sort(),
    [rows],
  );
  const filtered = useMemo(() => {
    const dq = q.trim().toLowerCase();
    return rows.filter((t) => {
      if (desc && (t.descripcion ?? "") !== desc) return false;
      if (dq) {
        const hay = [
          t.contraparte_nombre, t.contraparte_cuit, t.referencia,
          importeSearchText(t.importe), fmtMoney(t.importe),
        ].map((x) => String(x ?? "").toLowerCase()).join(" ");
        if (!hay.includes(dq)) return false;
      }
      return true;
    });
  }, [rows, desc, q]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortField === "importe") {
        const av = a.importe ?? -Infinity, bv = b.importe ?? -Infinity;
        return av < bv ? -dir : av > bv ? dir : 0;
      }
      const av = a.fecha_valor ?? "", bv = b.fecha_valor ?? "";
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  async function doConciliar() {
    if (!account) return;
    setConciliando(true); setErr(null);
    try {
      const res = await conciliar({ account });
      setResult(res);
      setConfirmOpen(false);
      await loadAll();
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setConciliando(false);
    }
  }

  // Trae TODAS las filas del filtro actual (paginando) para exportar.
  // CP-393: `onProgress` es opcional (descargas no necesitan mostrarlo) — lo
  // usa loadAll() para que el usuario vea cuánto va leído mientras espera.
  async function fetchAll(onProgress?: (n: number) => void, onTruncated?: () => void): Promise<Transaction[]> {
    const all: Transaction[] = [];
    let cur: string | undefined = undefined;
    let completo = false;
    for (let i = 0; i < TOPE_PAGINAS; i++) {  // tope de seguridad
      const r = await listTransactions({ ...commonFilter(), limit: FILAS_POR_PAGINA, cursor: cur });
      all.push(...r.items);
      onProgress?.(all.length);
      if (!r.cursor) { completo = true; break; }
      cur = r.cursor;
    }
    // Salió por el tope con páginas pendientes → incompleto.
    if (!completo) onTruncated?.();
    return all;
  }

  const filename = `transferencias_${account || "perdidas"}_${from || "ini"}_${to || "hoy"}`;

  async function downloadCsv() {
    setDownloading(true); setErr(null); setDlAnchor(null);
    try {
      const data = await fetchAll();
      const cols: (keyof Transaction)[] = [
        "fecha_valor", "account", "contraparte_cuit", "contraparte_nombre",
        "importe", "referencia", "descripcion", "status",
      ];
      const esc = (v: any) => {
        const s = v == null ? "" : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [cols.join(",")];
      for (const r of data) lines.push(cols.map((c) => esc((r as any)[c])).join(","));
      const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${filename}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setDownloading(false); }
  }

  async function downloadPdf() {
    setDownloading(true); setErr(null); setDlAnchor(null);
    try {
      const data = await fetchAll();
      const w = window.open("", "_blank");
      if (!w) { setErr("El navegador bloqueó la ventana de impresión (habilitá popups)."); return; }
      const esc = (s: any) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" } as any)[c]);
      const trs = data.map((r) =>
        `<tr><td>${esc(r.fecha_valor)}</td><td>${esc(r.account)}</td>` +
        `<td>${esc(r.contraparte_nombre)}<br><small>${esc(r.contraparte_cuit)}</small></td>` +
        `<td style="text-align:right">${esc(r.importe)}</td>` +
        `<td style="font-family:monospace">${esc(r.referencia)}</td><td>${esc(r.status)}</td></tr>`).join("");
      w.document.write(
        `<html><head><title>${esc(filename)}</title><style>` +
        `body{font-family:sans-serif;font-size:11px;padding:16px}table{border-collapse:collapse;width:100%}` +
        `th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top}th{background:#f0f0f0}` +
        `h2{font-size:15px;margin:0 0 4px}p{color:#555;margin:0 0 12px}</style></head><body>` +
        `<h2>Transferencias BIND — cuenta ${esc(account || "(perdidas)")}` +
        `${from ? ` · desde ${esc(from)}` : ""}${to ? ` · hasta ${esc(to)}` : ""}</h2>` +
        `<p>${data.length} transferencias · generado ${esc(new Date().toLocaleString("es-AR"))}</p>` +
        `<table><thead><tr><th>Fecha valor</th><th>Cuenta</th><th>Contraparte</th><th>Importe</th>` +
        `<th>Referencia</th><th>Estado</th></tr></thead><tbody>${trs}</tbody></table>` +
        `<script>window.onload=function(){window.print()}</script></body></html>`);
      w.document.close();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setDownloading(false); }
  }

  return (
    <>
      <Box
        sx={{
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25,
          mb: 2, p: 1.5,
          bgcolor: "#F7F6FC",
          border: "1px solid", borderColor: "rgba(91,47,235,0.12)",
          borderRadius: 3,
        }}
      >
        <TextField size="small" select fullWidth={false} label="Cuenta" value={account}
          onChange={(e) => setAccount(e.target.value as any)} sx={{ width: 168 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="45">1-749049/45</MenuItem>
          <MenuItem value="84">1-749049/84</MenuItem>
        </TextField>
        <TextField size="small" select fullWidth={false} label="Estado" value={status}
          onChange={(e) => {
            const v = e.target.value as any;
            setStatus(v);
            // CP-480: las pérdidas son pocas (índice propio) y pueden ser viejas → sin rango de fechas.
            if (v === "perdida") { setFrom(""); setTo(""); }
          }} sx={{ width: 150 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="pendiente">Pendientes</MenuItem>
          <MenuItem value="conciliada">Conciliadas</MenuItem>
          <MenuItem value="perdida">Perdidas</MenuItem>
          <MenuItem value="redisparada">Re-disparadas</MenuItem>
        </TextField>
        <TextField size="small" select fullWidth={false} label="Descripción" value={desc}
          onChange={(e) => setDesc(e.target.value)} sx={{ width: 190 }}>
          <MenuItem value="">Todas</MenuItem>
          {descOptions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <TextField size="small" type="date" fullWidth={false} label="Desde" value={from}
          onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 158 }} />
        <TextField size="small" type="date" fullWidth={false} label="Hasta" value={to}
          onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 158 }} />
        {!esRangoPorDefecto(from, to) && (
          <Tooltip title="Volver al rango por defecto (últimos 7 días)">
            <Button size="small" onClick={() => { const r = rangoPorDefecto(); setFrom(r.from); setTo(r.to); }}
              sx={{ minWidth: 0, color: "text.secondary" }}>Últimas 24 h</Button>
          </Tooltip>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <TextField size="small" fullWidth={false} placeholder="Buscar contraparte, importe o referencia"
          value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 300 }}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ), endAdornment: q ? (
            <IconButton size="small" onClick={() => setQ("")}><CloseIcon fontSize="small" /></IconButton>
          ) : undefined }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: "auto" }}>
          {loading && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
              <CircularProgress size={14} />
              <Typography variant="caption">Buscando…</Typography>
            </Stack>
          )}
          <Tooltip title="Refresh">
            <span><IconButton size="small" onClick={() => loadAll()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={needsAccount || downloading}
            onClick={(e) => setDlAnchor(e.currentTarget)} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            {downloading ? "Generando…" : "Descargar"}
          </Button>
          <Menu anchorEl={dlAnchor} open={!!dlAnchor} onClose={() => setDlAnchor(null)}>
            <MenuItem onClick={downloadCsv}>CSV</MenuItem>
            <MenuItem onClick={downloadPdf}>PDF (imprimir)</MenuItem>
          </Menu>
          <Button variant="contained" color="primary" size="small" disabled={!account || loading || conciliando}
            onClick={() => { setResult(null); setConfirmOpen(true); }} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            Re-conciliar ahora
          </Button>
        </Box>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {truncado && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Se cargaron {TOPE_FILAS.toLocaleString("es-AR")} transferencias (el máximo) y hay más: los resultados están <b>incompletos</b>.
          Acotá el rango de fechas para buscar en un período.
        </Alert>
      )}
      {result && (
        <Alert severity={(result.perdidas || 0) > 0 ? "error" : (result.redisparadas || 0) > 0 ? "warning" : "success"}
          onClose={() => setResult(null)} sx={{ mb: 2 }}>
          Re-conciliación: {result.conciliadas} conciliadas
          {(result.redisparadas || 0) > 0 ? <> • <b>{result.redisparadas} re-disparadas</b></> : null}
          {(result.perdidas || 0) > 0 ? <> • <b>{result.perdidas} perdidas (re-disparo falló)</b></> : null}
          {result.triggered_by ? ` (por ${result.triggered_by})` : ""}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell onClick={() => toggleSort("fecha_valor")}
                sx={{ cursor: "pointer", userSelect: "none", fontWeight: 700 }}>
                Fecha valor{sortArrow("fecha_valor")}
              </TableCell>
              <TableCell>Cuenta</TableCell>
              <TableCell>Contraparte</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="right" onClick={() => toggleSort("importe")}
                sx={{ cursor: "pointer", userSelect: "none", fontWeight: 700 }}>
                Importe{sortArrow("importe")}
              </TableCell>
              <TableCell>Referencia</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {needsAccount && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                Elegí una cuenta, o filtrá por <b>Perdidas</b> para ver todas las cuentas.
              </TableCell></TableRow>
            )}
            {/* CP-393: `loading` manda siempre, sin importar si hay filas viejas de
                un filtro anterior — si no, cambiar de filtro con una búsqueda de
                texto activa podía dejar la tabla sin spinner, sin filas y sin
                "sin resultados": en blanco, indistinguible de vacío. */}
            {!needsAccount && loading && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Buscando… {loadedCount} transferencias leídas
                  </Typography>
                </Stack>
              </TableCell></TableRow>
            )}
            {!needsAccount && !loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {searching ? "Sin resultados para la búsqueda." : "Sin transferencias para el filtro."}
              </TableCell></TableRow>
            )}
            {!needsAccount && !loading && sorted.slice(0, shown).map((t) => (
              <TableRow key={`${t.account}#${t.referencia}`} hover>
                <TableCell>{t.fecha_valor ?? "—"}</TableCell>
                <TableCell>{t.account}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.contraparte_nombre ?? "—"}</Typography>
                  {t.contraparte_cuit && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{t.contraparte_cuit}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>{t.descripcion ?? "—"}</TableCell>
                <TableCell align="right">{fmtMoney(t.importe)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{t.referencia}</TableCell>
                <TableCell><TxnStatusChip status={t.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {loading
            ? `Buscando… ${loadedCount} transferencias leídas`
            : `Búsqueda completa — mostrando ${Math.min(shown, sorted.length)} de ${sorted.length}`}
        </Typography>
        {sorted.length > shown && !loading && (
          <Button onClick={() => setShown((s) => s + 200)} variant="outlined" size="small">Mostrar más</Button>
        )}
      </Stack>

      <Dialog open={confirmOpen} onClose={() => !conciliando && setConfirmOpen(false)}>
        <DialogTitle>Re-conciliar cuenta {account} ahora</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La conciliación corre <b>automáticamente</b> en cada ciclo. Esto la fuerza ya mismo
            para la cuenta {account}: chequea el estado contra la base de Cucuru
            (<i>received_payments</i>) y <b>re-dispara solo las transferencias que falten</b>.
            No re-dispara las que ya están recibidas. ¿Continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={conciliando}>Cancelar</Button>
          <Button onClick={doConciliar} variant="contained" color="primary" disabled={conciliando}>
            {conciliando ? "Re-conciliando…" : "Re-conciliar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

